<template>
  <b-modal
    v-model="showModal"
    size="xl"
    hide-footer
    scrollable
    @show="onModalShow"
    @hide="onModalHide"
  >
    <template #modal-header="{ close }">
      <div class="d-flex align-items-center w-100 charge-modal-header" style="min-width: 0; gap: 8px;">
        <span class="drag-handle" @mousedown="onDragStart" title="Drag to move">⠿</span>
        <span class="text-truncate drag-title" style="font-size: 0.85rem; font-weight: 600; flex: 1; min-width: 0;">
          Charge Transactions — {{ bdxKey }}
        </span>
        <div class="d-flex align-items-center flex-shrink-0" style="gap: 6px;">
          <template v-if="!showForm && !showDeleteConfirm">
            <b-button
              v-if="isSuperuser"
              size="sm"
              class="limeiq-primary-btn"
              @click="openAddForm"
            >
              Add New
            </b-button>
            <b-button
              variant="outline-secondary"
              size="sm"
              :disabled="downloading"
              :title="downloading ? 'Downloading...' : 'Download'"
              @click="handleDownload"
            >
              <BaseIcon icon="download" tag="i" :font-scale="0.9" />
            </b-button>
          </template>
          <button
            type="button"
            @click="close()"
            style="background: none; border: none; padding: 0; margin: 0; line-height: 1; font-size: 1.4rem; color: #6c757d; cursor: pointer; display: flex; align-items: center;"
          >&times;</button>
        </div>
      </div>
    </template>

    <!-- Table view -->
    <template v-if="!showForm && !showDeleteConfirm && !viewingTransaction">

      <b-alert v-if="fetchError" variant="danger" show class="mb-2">{{ fetchError }}</b-alert>

      <div v-if="activeColumnFilters.length" class="mb-3 d-flex align-items-center flex-wrap" style="gap: 8px;">
        <b-badge
          v-for="f in activeColumnFilters"
          :key="f.key"
          class="text-primary border border-primary"
          pill
          style="padding: 4px 16px; font-size: 12px; font-weight: 600;"
        >
          {{ f.label }}: {{ f.value }}
          <a tabindex="0" role="button" class="text-danger ml-1" @click.prevent="clearColFilter(f.key)">
            <b-icon icon="x" :scale="1.5" />
          </a>
        </b-badge>
      </div>

      <b-table
        :items="filteredTransactions"
        :fields="tableFields"
        :busy="loading"
        responsive
        show-empty
        empty-text="No transactions found for this key."
        class="charge-table"
        @sort-changed="onSortChanged"
      >
        <template #table-busy>
          <div class="text-center my-3">
            <b-spinner small />
            <span class="ml-2">Loading...</span>
          </div>
        </template>

        <template #head(created_on)>
          <div class="d-flex align-items-center">
            Invoice Charge Date
            <a id="charge-filter-created-on" tabindex="0" role="button" class="ml-2 charge-filter-btn">
              <b-icon icon="search" :scale="0.85" />
            </a>
            <span class="charge-sort-icons ml-1">
              <b-icon icon="caret-up-fill" :class="sortState.sortBy === 'created_on' && !sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
              <b-icon icon="caret-down-fill" :class="sortState.sortBy === 'created_on' && sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
            </span>
            <b-popover target="charge-filter-created-on" triggers="click blur" placement="rightbottom">
              <form class="p-2" @submit.prevent.stop="applyColFilter('created_on', colFilterInput.created_on)">
                <b-form-input v-model="colFilterInput.created_on" size="sm" placeholder="Search by date" autofocus />
                <b-button variant="primary" size="sm" type="submit" class="mt-2">Apply</b-button>
              </form>
            </b-popover>
          </div>
        </template>

        <template #head(reporting_period)>
          <div class="d-flex align-items-center">
            Reporting Period
            <a id="charge-filter-reporting-period" tabindex="0" role="button" class="ml-2 charge-filter-btn">
              <b-icon icon="search" :scale="0.85" />
            </a>
            <span class="charge-sort-icons ml-1">
              <b-icon icon="caret-up-fill" :class="sortState.sortBy === 'reporting_period' && !sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
              <b-icon icon="caret-down-fill" :class="sortState.sortBy === 'reporting_period' && sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
            </span>
            <b-popover target="charge-filter-reporting-period" triggers="click blur" placement="rightbottom">
              <form class="p-2" @submit.prevent.stop="applyColFilter('reporting_period', colFilterInput.reporting_period)">
                <b-form-input v-model="colFilterInput.reporting_period" size="sm" placeholder="Search by date" autofocus />
                <b-button variant="primary" size="sm" type="submit" class="mt-2">Apply</b-button>
              </form>
            </b-popover>
          </div>
        </template>

        <template #head(billing_transaction_date)>
          <div class="d-flex align-items-center">
            Billing Transaction Date
            <a id="charge-filter-btd" tabindex="0" role="button" class="ml-2 charge-filter-btn">
              <b-icon icon="search" :scale="0.85" />
            </a>
            <span class="charge-sort-icons ml-1">
              <b-icon icon="caret-up-fill" :class="sortState.sortBy === 'billing_transaction_date' && !sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
              <b-icon icon="caret-down-fill" :class="sortState.sortBy === 'billing_transaction_date' && sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
            </span>
            <b-popover target="charge-filter-btd" triggers="click blur" placement="rightbottom">
              <form class="p-2" @submit.prevent.stop="applyColFilter('billing_transaction_date', colFilterInput.billing_transaction_date)">
                <b-form-input v-model="colFilterInput.billing_transaction_date" size="sm" placeholder="Search by date" autofocus />
                <b-button variant="primary" size="sm" type="submit" class="mt-2">Apply</b-button>
              </form>
            </b-popover>
          </div>
        </template>

        <template #head(amount)>
          <div class="d-flex align-items-center">
            Invoice Charge Amount
            <a id="charge-filter-amount" tabindex="0" role="button" class="ml-2 charge-filter-btn">
              <b-icon icon="search" :scale="0.85" />
            </a>
            <span class="charge-sort-icons ml-1">
              <b-icon icon="caret-up-fill" :class="sortState.sortBy === 'amount' && !sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
              <b-icon icon="caret-down-fill" :class="sortState.sortBy === 'amount' && sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
            </span>
            <b-popover target="charge-filter-amount" triggers="click blur" placement="rightbottom">
              <form class="p-2" @submit.prevent.stop="applyColFilter('amount', colFilterInput.amount)">
                <b-form-input v-model="colFilterInput.amount" size="sm" placeholder="Search by amount" autofocus />
                <b-button variant="primary" size="sm" type="submit" class="mt-2">Apply</b-button>
              </form>
            </b-popover>
          </div>
        </template>

        <template #head(credit_amount)>
          <div class="d-flex align-items-center">
            Credit Amount
            <a id="charge-filter-credit-amount" tabindex="0" role="button" class="ml-2 charge-filter-btn">
              <b-icon icon="search" :scale="0.85" />
            </a>
            <span class="charge-sort-icons ml-1">
              <b-icon icon="caret-up-fill" :class="sortState.sortBy === 'credit_amount' && !sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
              <b-icon icon="caret-down-fill" :class="sortState.sortBy === 'credit_amount' && sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
            </span>
            <b-popover target="charge-filter-credit-amount" triggers="click blur" placement="rightbottom">
              <form class="p-2" @submit.prevent.stop="applyColFilter('credit_amount', colFilterInput.credit_amount)">
                <b-form-input v-model="colFilterInput.credit_amount" size="sm" placeholder="Search by amount" autofocus />
                <b-button variant="primary" size="sm" type="submit" class="mt-2">Apply</b-button>
              </form>
            </b-popover>
          </div>
        </template>

        <template #head(credit_date)>
          <div class="d-flex align-items-center">
            Credit Date
            <a id="charge-filter-credit-date" tabindex="0" role="button" class="ml-2 charge-filter-btn">
              <b-icon icon="search" :scale="0.85" />
            </a>
            <span class="charge-sort-icons ml-1">
              <b-icon icon="caret-up-fill" :class="sortState.sortBy === 'credit_date' && !sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
              <b-icon icon="caret-down-fill" :class="sortState.sortBy === 'credit_date' && sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
            </span>
            <b-popover target="charge-filter-credit-date" triggers="click blur" placement="rightbottom">
              <form class="p-2" @submit.prevent.stop="applyColFilter('credit_date', colFilterInput.credit_date)">
                <b-form-input v-model="colFilterInput.credit_date" size="sm" placeholder="Search by date" autofocus />
                <b-button variant="primary" size="sm" type="submit" class="mt-2">Apply</b-button>
              </form>
            </b-popover>
          </div>
        </template>

        <template #head(invoice_date)>
          <div class="d-flex align-items-center">
            Invoice Date
            <a id="charge-filter-invoice-date" tabindex="0" role="button" class="ml-2 charge-filter-btn">
              <b-icon icon="search" :scale="0.85" />
            </a>
            <span class="charge-sort-icons ml-1">
              <b-icon icon="caret-up-fill" :class="sortState.sortBy === 'invoice_date' && !sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
              <b-icon icon="caret-down-fill" :class="sortState.sortBy === 'invoice_date' && sortState.sortDesc ? 'sort-active' : 'sort-inactive'" />
            </span>
            <b-popover target="charge-filter-invoice-date" triggers="click blur" placement="rightbottom">
              <form class="p-2" @submit.prevent.stop="applyColFilter('invoice_date', colFilterInput.invoice_date)">
                <b-form-input v-model="colFilterInput.invoice_date" size="sm" placeholder="Search by date" autofocus />
                <b-button variant="primary" size="sm" type="submit" class="mt-2">Apply</b-button>
              </form>
            </b-popover>
          </div>
        </template>

        <template #cell(created_on)="{ item }">
          <template v-if="item.created_on">
            <div>{{ item.created_on.slice(0, 10) }}</div>
          </template>
          <template v-else>—</template>
        </template>

        <template #cell(invoice_date)="{ item }">
          {{ item.invoice_date ? String(item.invoice_date).slice(0, 10) : '—' }}
        </template>

        <template #head(invoice_charge_period)>
          <div class="d-flex align-items-center">
            Invoice Charge Period
            <a id="charge-filter-invoice-charge-period" tabindex="0" role="button" class="ml-2 charge-filter-btn">
              <b-icon icon="search" :scale="0.85" />
            </a>
            <b-popover target="charge-filter-invoice-charge-period" triggers="click blur" placement="rightbottom">
              <form class="p-2" @submit.prevent.stop="applyColFilter('invoice_charge_period', colFilterInput.invoice_charge_period)">
                <b-form-select v-model="colFilterInput.invoice_charge_period" size="sm">
                  <b-form-select-option value="">All</b-form-select-option>
                  <b-form-select-option v-for="period in availableChargePeriods" :key="period" :value="period">{{ period }}</b-form-select-option>
                </b-form-select>
                <b-button variant="primary" size="sm" type="submit" class="mt-2">Apply</b-button>
              </form>
            </b-popover>
          </div>
        </template>

        <template #cell(invoice_charge_period)="{ item }">
          {{ formatReportingPeriod(item.reporting_period) || '—' }}
        </template>

        <template #cell(amount)="{ item }">
          {{ item.amount != null ? Math.abs(parseFloat(item.amount)).toFixed(2) : '—' }}
        </template>

        <template #cell(credit_amount)="{ item }">
          {{ item.credit_amount != null ? parseFloat(item.credit_amount).toFixed(2) : '—' }}
        </template>

        <template #cell(actions)="{ item }">
          <a
            v-if="item.credit_amount != null"
            role="button"
            class="mr-2 charge-action-icon"
            title="View Credit Details"
            @click="openViewCredit(item)"
          >
            <BaseIcon icon="eye" tag="i" :font-scale="0.9" />
          </a>
          <template v-if="isSuperuser">
            <a
              v-if="item.invoice != null && item.credit_amount == null"
              role="button"
              class="mr-2 charge-action-icon"
              title="Credit"
              @click="openCreditForm(item)"
            >
              <BaseIcon icon="cash" tag="i" :font-scale="0.9" />
            </a>
            <a
              v-if="canDelete(item)"
              role="button"
              class="charge-action-icon"
              title="Delete"
              @click="openDeleteConfirm(item)"
            >
              <BaseIcon icon="trash" tag="i" :font-scale="0.9" />
            </a>
          </template>
        </template>
      </b-table>

      <div
        v-if="totalCount > pageSize"
        class="d-flex justify-content-between align-items-center mt-2"
      >
        <span class="text-muted small">Showing {{ transactions.length }} of {{ totalCount }}</span>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalCount"
          :per-page="pageSize"
          size="sm"
          @change="handlePageChange"
        />
      </div>

      <div class="d-flex justify-content-end mt-3">
        <b-button variant="secondary" @click="close">Close</b-button>
      </div>
    </template>

    <!-- Add / Edit / Credit form -->
    <template v-else-if="showForm">
      <h5 class="mb-3 charge-section-title">
        {{ creditingTransaction ? 'Credit Invoice Charge' : editingTransaction ? 'Edit Invoice Charge' : 'New Invoice Charge' }}
      </h5>

      <b-form @submit.prevent="handleFormSubmit">
        <b-row>
          <b-col md="4">
            <b-form-group label="LimeIQ Key">
              <b-form-input :value="bdxKey" disabled />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Invoice Charge Date *">
              <b-form-input v-model="form.created_on" type="date" required :disabled="!!creditingTransaction" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <b-form-group label="Reporting Period *">
              <b-form-select
                v-if="!editingTransaction && !creditingTransaction"
                v-model="form.reporting_period"
                :disabled="loadingPeriods"
                required
              >
                <template #first>
                  <b-form-select-option value="">{{ loadingPeriods ? 'Loading...' : 'Select Period' }}</b-form-select-option>
                </template>
                <b-form-select-option
                  v-for="p in availablePeriods"
                  :key="p.value"
                  :value="p.value"
                >{{ p.label }}</b-form-select-option>
              </b-form-select>
              <b-form-input v-else v-model="form.reporting_period" type="month" :disabled="!!creditingTransaction" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Amount *">
              <b-form-input v-model="form.amount" type="number" step="0.01" min="0" required placeholder="Enter amount" :disabled="!!creditingTransaction" />
            </b-form-group>
          </b-col>
          <b-col md="4">
            <b-form-group label="Currency Type">
              <b-form-select v-model="form.currency_type" disabled>
                <template #first>
                  <b-form-select-option value="">Select Currency</b-form-select-option>
                </template>
                <b-form-select-option
                  v-for="currency in BDX_CURRENCY_TYPES"
                  :key="currency"
                  :value="currency"
                >{{ currency }}</b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="4">
            <b-form-group label="Source">
              <b-form-input value="Manual" disabled />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row v-if="creditingTransaction">
          <b-col md="6">
            <b-form-group label="Credit Description">
              <b-form-textarea v-model="form.credit_description" rows="2" placeholder="Optional credit description" />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row v-if="creditingTransaction">
          <b-col md="4">
            <b-form-group label="Credit Amount *">
              <b-form-input v-model="form.credit_amount" type="number" step="0.01" min="0" placeholder="Enter credit amount" required />
            </b-form-group>
          </b-col>
          <b-col v-if="form.credit_date" md="4">
            <b-form-group label="Credit Date">
              <b-form-input :value="form.credit_date" disabled />
            </b-form-group>
          </b-col>
        </b-row>

        <b-row>
          <b-col md="12">
            <b-form-group label="Reason for Adjustment *">
              <b-form-textarea
                v-model="form.reason_for_adjustment"
                rows="2"
                placeholder="Required — describe why this change is being made"
                required
              />
            </b-form-group>
          </b-col>
        </b-row>

        <div v-if="formError" class="alert alert-danger py-2 small">{{ formError }}</div>

        <div class="d-flex justify-content-between mt-3">
          <b-button type="submit" class="limeiq-primary-btn" :disabled="submitting">
            {{ submitting ? 'Saving...' : creditingTransaction ? 'Apply Credit' : 'Submit' }}
          </b-button>
          <b-button variant="secondary" @click="cancelForm">Cancel</b-button>
        </div>
      </b-form>
    </template>

    <!-- Credit details view (read-only) -->
    <template v-else-if="viewingTransaction">
      <h5 class="mb-3 charge-section-title">Credit Details</h5>
      <b-row>
        <b-col md="4">
          <b-form-group label="LimeIQ Key">
            <b-form-input :value="viewingTransaction.bdx_key" disabled />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Reporting Period">
            <b-form-input :value="viewingTransaction.reporting_period" disabled />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Invoice Charge Amount">
            <b-form-input :value="viewingTransaction.amount" disabled />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="4">
          <b-form-group label="Credit Amount">
            <b-form-input :value="viewingTransaction.credit_amount != null ? parseFloat(viewingTransaction.credit_amount).toFixed(2) : ''" disabled />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Credit Date">
            <b-form-input :value="viewingTransaction.credit_date || ''" disabled />
          </b-form-group>
        </b-col>
        <b-col md="4">
          <b-form-group label="Currency Type">
            <b-form-input :value="viewingTransaction.currency_type || ''" disabled />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row>
        <b-col md="12">
          <b-form-group label="Credit Description">
            <b-form-textarea :value="viewingTransaction.credit_description || ''" rows="3" disabled />
          </b-form-group>
        </b-col>
      </b-row>
      <div class="d-flex justify-content-end mt-3">
        <b-button variant="secondary" @click="closeViewCredit">Close</b-button>
      </div>
    </template>

    <!-- Delete confirmation with reason -->
    <template v-else-if="showDeleteConfirm">
      <h5 class="mb-3 charge-section-title">Delete Invoice Charge</h5>
      <p class="text-muted mb-3" style="font-size: 1rem;">
        You are about to delete the invoice charge for reporting period
        <strong>{{ deletingItem ? deletingItem.reporting_period : '' }}</strong>.
        This action cannot be undone.
      </p>

      <div v-if="deletePreviewLoading" class="text-muted small mb-3">Checking related charges...</div>
      <template v-else-if="deletePreview">
        <p
          v-if="deletePreview.charge.invoice_status === 'Approved'"
          class="alert alert-warning py-2 small mb-3"
        >
          This charge is on approved invoice
          <strong>{{ deletePreview.charge.invoice_reference }}</strong>, which has not been dispatched.
          Deleting it returns that invoice to draft so it can be approved again.
        </p>

        <template v-if="deletePreview.splits.length">
          <p class="mb-2" style="font-size: 0.95rem;">
            This charge is one of {{ deletePreview.splits.length + 1 }} syndicate splits of the same
            LimeIQ key and reporting period (total {{ deletePreview.charge.currency_type }}
            {{ deletePreview.splits_total }}). Review the other splits before choosing what to delete:
          </p>
          <b-table-simple small bordered responsive class="mb-3 delete-splits-table">
            <b-thead>
              <b-tr>
                <b-th>Syndicate</b-th>
                <b-th>Underwriter</b-th>
                <b-th class="text-right">Amount</b-th>
                <b-th>Invoice</b-th>
                <b-th>Can delete</b-th>
              </b-tr>
            </b-thead>
            <b-tbody>
              <b-tr class="delete-splits-current">
                <b-td>{{ deletePreview.charge.syndicate_number || '—' }} (this charge)</b-td>
                <b-td>{{ deletePreview.charge.underwriter || '—' }}</b-td>
                <b-td class="text-right">{{ deletePreview.charge.currency_type }} {{ deletePreview.charge.amount }}</b-td>
                <b-td>{{ deletePreview.charge.invoice_reference || 'Draft' }} ({{ deletePreview.charge.invoice_status }})</b-td>
                <b-td>Yes</b-td>
              </b-tr>
              <b-tr v-for="split in deletePreview.splits" :key="split.id">
                <b-td>{{ split.syndicate_number || '—' }}</b-td>
                <b-td>{{ split.underwriter || '—' }}</b-td>
                <b-td class="text-right">{{ split.currency_type }} {{ split.amount }}</b-td>
                <b-td>{{ split.invoice_reference || 'Draft' }} ({{ split.invoice_status }})</b-td>
                <b-td>{{ split.deletable ? 'Yes' : split.blocked_reason }}</b-td>
              </b-tr>
            </b-tbody>
          </b-table-simple>

          <b-form-group label="What do you want to delete?">
            <b-form-radio v-model="deleteScope" value="this">
              This charge only (syndicate {{ deletePreview.charge.syndicate_number || '—' }})
            </b-form-radio>
            <small v-if="deleteScope === 'this'" class="d-block text-muted ml-4 mb-2">
              The remaining splits will no longer add up to the full charge, and a later slip share
              update may re-create this split.
            </small>
            <b-form-radio v-model="deleteScope" value="all" :disabled="!allSplitsDeletable">
              This charge and the {{ deletePreview.splits.length }} other syndicate split(s)
            </b-form-radio>
            <small v-if="!allSplitsDeletable" class="d-block text-muted ml-4">
              Not available: at least one split can no longer be deleted (see above).
            </small>
          </b-form-group>
        </template>
      </template>

      <b-form @submit.prevent="handleDelete">
        <b-form-group label="Reason for Adjustment *">
          <b-form-textarea
            v-model="deleteReason"
            rows="3"
            placeholder="Required — describe why this charge is being deleted"
            required
          />
        </b-form-group>

        <div v-if="deleteError" class="alert alert-danger py-2 small">{{ deleteError }}</div>

        <div class="d-flex justify-content-between mt-3">
          <b-button type="submit" class="limeiq-primary-btn" :disabled="deletingId !== null">
            {{ deletingId !== null ? 'Deleting...' : 'Confirm Delete' }}
          </b-button>
          <b-button variant="secondary" @click="cancelDelete">Cancel</b-button>
        </div>
      </b-form>
    </template>

  </b-modal>
</template>

<script>
import Vue from 'vue'
import { defineComponent, ref, computed, reactive } from '@vue/composition-api'
import { BaseIcon } from '@components/base'
import { InvoiceChargeService } from '@services'
import { BDX_CURRENCY_TYPES } from '@constants'

const TABLE_FIELDS = [
  { key: 'created_on', label: 'Invoice Charge Date', sortable: true },
  { key: 'reporting_period', label: 'Reporting Period', sortable: true },
  { key: 'billing_transaction_date', label: 'Billing Transaction Date', sortable: true },
  { key: 'invoice_charge_period', label: 'Invoice Charge Period', sortable: false },
  { key: 'amount', label: 'Invoice Charge Amount', sortable: true },
  { key: 'credit_amount', label: 'Credit Amount', sortable: true },
  { key: 'credit_date', label: 'Credit Date', sortable: true },
  { key: 'invoice_date', label: 'Invoice Date', sortable: true },
  { key: 'actions', label: '', thStyle: { width: '100px' } }
]

const MONTH_ABBREVIATIONS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

// reporting_period is a plain 'YYYY-MM-DD' date string with no time/offset.
// Parsing it with `new Date(...)` treats it as UTC midnight, which then
// renders in the browser's local timezone and can roll back to the previous
// month — so this slices the string instead, matching the raw
// "Reporting Period" column and the invoice export's "Report Period".
const formatReportingPeriod = (reportingPeriod) => {
  if (!reportingPeriod) return null
  const [year, month] = reportingPeriod.slice(0, 7).split('-')
  return `${MONTH_ABBREVIATIONS[Number(month) - 1]} ${year}`
}

const today = () => new Date().toISOString().slice(0, 10)

const EMPTY_FORM = () => ({
  created_on: today(),
  tpa: '',
  reporting_period: '',
  amount: '',
  currency_type: '',
  source: '',
  credit_description: '',
  credit_amount: '',
  credit_date: '',
  billing_transaction_date: '',
  billed_bdx_key: '',
  reason_for_adjustment: ''
})

export default defineComponent({
  name: 'ChargeTransactionsModal',
  components: { BaseIcon },
  props: {
    value: { type: Boolean, default: false },
    bdxKey: { type: String, default: '' },
    currencyType: { type: String, default: '' },
    tpa: { type: String, default: '' }
  },
  emits: ['input'],
  setup(props, { emit, root }) {
    const showModal = computed({
      get: () => props.value,
      set: (v) => emit('input', v)
    })

    const isSuperuser = computed(() => root.$store.getters.user.is_superuser)

    const dragOffset = ref({ x: 0, y: 0 })
    const dragStart = ref({ mouseX: 0, mouseY: 0 })
    const isDragging = ref(false)

    const getDialog = () => document.querySelector('.modal.show .modal-dialog')

    const onDragStart = (e) => {
      e.preventDefault()
      const handleEl = e.currentTarget
      isDragging.value = true
      dragStart.value = { mouseX: e.clientX - dragOffset.value.x, mouseY: e.clientY - dragOffset.value.y }

      handleEl.style.cursor = 'grabbing'
      document.body.style.userSelect = 'none'

      const onMove = (ev) => {
        if (!isDragging.value) return
        dragOffset.value.x = ev.clientX - dragStart.value.mouseX
        dragOffset.value.y = ev.clientY - dragStart.value.mouseY
        const dialog = getDialog()
        if (dialog) {
          dialog.style.transform = `translate(${dragOffset.value.x}px, ${dragOffset.value.y}px)`
        }
      }

      const onUp = () => {
        isDragging.value = false
        handleEl.style.cursor = 'grab'
        document.body.style.userSelect = ''
        document.removeEventListener('mousemove', onMove)
        document.removeEventListener('mouseup', onUp)
      }

      document.addEventListener('mousemove', onMove)
      document.addEventListener('mouseup', onUp)
    }

    const loading = ref(false)
    const transactions = ref([])
    const totalCount = ref(0)
    const currentPage = ref(1)
    const pageSize = ref(20)
    const deletingId = ref(null)
    const downloading = ref(false)

    const showForm = ref(false)
    const editingTransaction = ref(null)
    const creditingTransaction = ref(null)
    const submitting = ref(false)
    const formError = ref('')

    const showDeleteConfirm = ref(false)
    const deletingItem = ref(null)
    const deleteReason = ref('')
    const deleteError = ref('')
    // LIQ-2541: the other syndicate splits of a LIMOSS charge, previewed before delete.
    const deletePreview = ref(null)
    const deletePreviewLoading = ref(false)
    const deleteScope = ref('this')
    const allSplitsDeletable = computed(() =>
      !!deletePreview.value && deletePreview.value.splits.every(s => s.deletable)
    )

    // The same rule for Market and LIMOSS: same billing month as the charge's
    // creation (compared in UTC, like the server) and its invoice, if any, not
    // yet dispatched - an approved invoice then returns to draft.
    const utcMonth = (value) => new Date(value).toISOString().slice(0, 7)
    const canDelete = (item) => {
      if (!item.created_on || item.credit_amount != null) return false
      if (utcMonth(item.created_on) !== utcMonth(Date.now())) return false
      return item.invoice == null || !item.invoice_dispatched_at
    }

    const form = ref(EMPTY_FORM())
    const tableFields = TABLE_FIELDS
    const fetchError = ref('')

    const FILTER_LABELS = {
      created_on: 'Invoice Charge Date',
      reporting_period: 'Reporting Period',
      billing_transaction_date: 'Billing Transaction Date',
      amount: 'Invoice Charge Amount',
      credit_amount: 'Credit Amount',
      credit_date: 'Credit Date',
      invoice_date: 'Invoice Date',
      invoice_charge_period: 'Invoice Charge Period'
    }

    const FILTER_POPOVER_IDS = {
      created_on: 'charge-filter-created-on',
      reporting_period: 'charge-filter-reporting-period',
      billing_transaction_date: 'charge-filter-btd',
      amount: 'charge-filter-amount',
      credit_amount: 'charge-filter-credit-amount',
      credit_date: 'charge-filter-credit-date',
      invoice_date: 'charge-filter-invoice-date',
      invoice_charge_period: 'charge-filter-invoice-charge-period'
    }

    const columnFilters = reactive({
      created_on: '',
      reporting_period: '',
      billing_transaction_date: '',
      amount: '',
      credit_amount: '',
      credit_date: '',
      invoice_date: '',
      invoice_charge_period: ''
    })

    const colFilterInput = reactive({
      created_on: '',
      reporting_period: '',
      billing_transaction_date: '',
      amount: '',
      credit_amount: '',
      credit_date: '',
      invoice_date: '',
      invoice_charge_period: ''
    })

    const activeColumnFilters = computed(() =>
      Object.entries(columnFilters)
        .filter(([, val]) => !!val)
        .map(([key, val]) => ({ key, label: FILTER_LABELS[key], value: val }))
    )

    const sortState = reactive({ sortBy: '', sortDesc: false })
    const onSortChanged = ({ sortBy, sortDesc }) => {
      sortState.sortBy = sortBy
      sortState.sortDesc = sortDesc
    }

    const applyColFilter = (key, value) => {
      columnFilters[key] = value
      const popoverId = FILTER_POPOVER_IDS[key]
      if (popoverId) root.$root.$emit('bv::hide::popover', popoverId)
    }

    const clearColFilter = (key) => {
      columnFilters[key] = ''
      colFilterInput[key] = ''
    }

    const availableChargePeriods = computed(() => {
      const seen = new Set()
      return transactions.value
        .map(t => formatReportingPeriod(t.reporting_period))
        .filter(p => p && !seen.has(p) && seen.add(p))
    })

    const matchesColumnFilters = (item, filters) => {
      if (item.amount != null && parseFloat(item.amount) < 0 && item.invoice == null) return false
      return Object.entries(filters).every(([key, val]) => {
        if (!val) return true
        if (key === 'invoice_charge_period') {
          const formatted = formatReportingPeriod(item.reporting_period) || ''
          return formatted.toLowerCase().includes(String(val).toLowerCase())
        }
        return String(item[key] || '').toLowerCase().includes(String(val).toLowerCase())
      })
    }

    const filteredTransactions = computed(() => transactions.value.filter(item => matchesColumnFilters(item, columnFilters)))

    // The modal only holds one page; the download needs every row for the key.
    const fetchAllTransactions = async (bdxKey) => {
      const all = []
      let page = 1
      let count = 0
      do {
        const { data } = await InvoiceChargeService.list(bdxKey, { page, page_size: pageSize.value })
        const results = data.results || []
        count = data.count || 0
        all.push(...results)
        if (!results.length) break
        page += 1
      } while (all.length < count)
      return all
    }

    const fetchTransactions = async () => {
      if (!props.bdxKey) return
      loading.value = true
      fetchError.value = ''
      try {
        const { data } = await InvoiceChargeService.list(props.bdxKey, {
          page: currentPage.value,
          page_size: pageSize.value
        })
        transactions.value = data.results || []
        totalCount.value = data.count || 0
      } catch (err) {
        transactions.value = []
        totalCount.value = 0
        const msg = err && err.response && err.response.data
        fetchError.value = msg ? JSON.stringify(msg) : (err && err.message) || 'Failed to load invoice charges.'
      } finally {
        loading.value = false
      }
    }

    const onModalShow = () => {
      dragOffset.value = { x: 0, y: 0 }
      const dialog = getDialog()
      if (dialog) dialog.style.transform = ''
      fetchTransactions()
    }

    const onModalHide = () => {
      showForm.value = false
      showDeleteConfirm.value = false
      editingTransaction.value = null
      creditingTransaction.value = null
      viewingTransaction.value = null
      deletingItem.value = null
      deleteReason.value = ''
      deleteError.value = ''
      form.value = EMPTY_FORM()
      formError.value = ''
      transactions.value = []
      totalCount.value = 0
      currentPage.value = 1
      Object.keys(columnFilters).forEach(k => { columnFilters[k] = '' })
      Object.keys(colFilterInput).forEach(k => { colFilterInput[k] = '' })

    }

    const close = () => { showModal.value = false }

    const handlePageChange = (page) => {
      currentPage.value = page
      fetchTransactions()
    }

    const availablePeriods = ref([])
    const loadingPeriods = ref(false)

    const openAddForm = async () => {
      editingTransaction.value = null
      creditingTransaction.value = null
      form.value = { ...EMPTY_FORM(), currency_type: props.currencyType || '', tpa: props.tpa || '', source: 'Manual' }
      formError.value = ''
      showForm.value = true
      loadingPeriods.value = true
      try {
        const { data } = await InvoiceChargeService.availablePeriods(props.bdxKey)
        availablePeriods.value = data.periods || []
      } catch {
        availablePeriods.value = []
      } finally {
        loadingPeriods.value = false
      }
    }

    const openEditForm = (item) => {
      editingTransaction.value = item
      creditingTransaction.value = null
      form.value = {
        created_on: item.created_on ? item.created_on.slice(0, 10) : today(),
        tpa: item.tpa || '',
        reporting_period: item.reporting_period ? item.reporting_period.slice(0, 7) : '',
        amount: item.amount || '',
        currency_type: item.currency_type || '',
        source: item.source || '',
        credit_description: item.credit_description || '',
        credit_amount: item.credit_amount || '',
        credit_date: item.credit_date || '',
        billing_transaction_date: item.billing_transaction_date || '',
        billed_bdx_key: item.billed_bdx_key || '',
        reason_for_adjustment: ''
      }
      formError.value = ''
      showForm.value = true
    }

    const viewingTransaction = ref(null)

    const openViewCredit = (item) => {
      viewingTransaction.value = item
      showForm.value = false
      showDeleteConfirm.value = false
      editingTransaction.value = null
      creditingTransaction.value = null
    }

    const closeViewCredit = () => {
      viewingTransaction.value = null
    }

    const openCreditForm = (item) => {
      creditingTransaction.value = item
      editingTransaction.value = null
      form.value = {
        created_on: item.created_on ? item.created_on.slice(0, 10) : today(),
        tpa: item.tpa || '',
        reporting_period: item.reporting_period ? item.reporting_period.slice(0, 7) : '',
        amount: item.amount || '',
        currency_type: item.currency_type || '',
        source: item.source || '',
        credit_description: item.credit_description || '',
        credit_amount: item.credit_amount || '',
        credit_date: item.credit_date || '',
        billing_transaction_date: item.billing_transaction_date || '',
        billed_bdx_key: item.billed_bdx_key || '',
        reason_for_adjustment: ''
      }
      formError.value = ''
      showForm.value = true
    }

    const cancelForm = () => {
      showForm.value = false
      editingTransaction.value = null
      creditingTransaction.value = null
      form.value = EMPTY_FORM()
      formError.value = ''
    }

    const buildPayload = () => {
      const f = form.value
      return {
        bdx_key: props.bdxKey,
        charge_type: 'Invoice Charge',
        tpa: f.tpa || null,
        reporting_period: f.reporting_period || null,
        amount: f.amount || null,
        currency_type: f.currency_type || null,
        source: f.source || null,
        billing_type: f.billing_type || null,
        credit_description: f.credit_description || null,
        credit_amount: f.credit_amount || null,
        credit_date: f.credit_date || null,
        reason_for_adjustment: f.reason_for_adjustment
      }
    }

    const handleFormSubmit = async () => {
      submitting.value = true
      formError.value = ''
      try {
        if (creditingTransaction.value) {
          await InvoiceChargeService.credit(creditingTransaction.value.id, {
            amount: form.value.amount || null,
            credit_description: form.value.credit_description || null,
            credit_amount: form.value.credit_amount || null,
            reason_for_adjustment: form.value.reason_for_adjustment
          })
        } else if (editingTransaction.value) {
          const payload = buildPayload()
          await InvoiceChargeService.update(editingTransaction.value.id, payload)
        } else {
          const payload = buildPayload()
          await InvoiceChargeService.create(payload)
        }
        showForm.value = false
        editingTransaction.value = null
        creditingTransaction.value = null
        form.value = EMPTY_FORM()
        currentPage.value = 1
        await fetchTransactions()
        Vue.prototype.$emitter.emit('invoice-charge:success')
      } catch (err) {
        const errData = err && err.response && err.response.data
        let msg = 'An error occurred.'
        if (errData) {
          if (typeof errData === 'string') msg = errData
          else if (Array.isArray(errData)) msg = errData.join(' ')
          else if (errData.error) msg = Array.isArray(errData.error) ? errData.error.join(' ') : errData.error
          else if (errData.message) msg = errData.message
          else if (errData.non_field_errors) msg = Array.isArray(errData.non_field_errors) ? errData.non_field_errors.join(' ') : errData.non_field_errors
          else msg = Object.values(errData).flat().join(' ')
        }
        formError.value = msg
      } finally {
        submitting.value = false
      }
    }

    // Each preview request gets its own sequence number. Only the latest
    // request may touch the dialog: cancelling while a preview is loading and
    // opening another charge must not let the first response describe the
    // first charge's splits under the second charge's Confirm Delete.
    let deletePreviewSeq = 0

    const openDeleteConfirm = async (item) => {
      const seq = ++deletePreviewSeq
      deletingItem.value = item
      deleteReason.value = ''
      deleteError.value = ''
      deleteScope.value = 'this'
      deletePreview.value = null
      showDeleteConfirm.value = true
      deletePreviewLoading.value = true
      try {
        const { data } = await InvoiceChargeService.deletePreview(item.id)
        if (seq !== deletePreviewSeq) return
        deletePreview.value = data
      } catch (err) {
        if (seq !== deletePreviewSeq) return
        const errData = err && err.response && err.response.data
        deleteError.value = (errData && errData.error) || 'Could not load the related charges.'
      } finally {
        if (seq === deletePreviewSeq) deletePreviewLoading.value = false
      }
    }

    const cancelDelete = () => {
      // Orphan any preview still in flight for the dialog being closed.
      deletePreviewSeq += 1
      deletePreviewLoading.value = false
      deletePreview.value = null
      showDeleteConfirm.value = false
      deletingItem.value = null
      deleteReason.value = ''
      deleteError.value = ''
    }

    const handleDelete = async () => {
      if (!deleteReason.value.trim()) {
        deleteError.value = 'Reason for adjustment is required.'
        return
      }
      deletingId.value = deletingItem.value.id
      deleteError.value = ''
      try {
        await InvoiceChargeService.destroy(deletingItem.value.id, {
          reason_for_adjustment: deleteReason.value,
          include_splits: deleteScope.value === 'all'
        })
        showDeleteConfirm.value = false
        deletingItem.value = null
        deleteReason.value = ''
        await fetchTransactions()
      } catch (err) {
        const errData = err && err.response && err.response.data
        const msg = (errData && errData.error) || (errData && errData.message) || errData || 'Failed to delete.'
        deleteError.value = typeof msg === 'string' ? msg : JSON.stringify(msg)
      } finally {
        deletingId.value = null
      }
    }

    const handleDownload = async () => {
      downloading.value = true
      try {
        // Snapshot: the prop and filters can change while the pages load.
        const bdxKey = props.bdxKey
        const filters = { ...columnFilters }
        const allTransactions = await fetchAllTransactions(bdxKey)
        const rows = allTransactions.filter(t => matchesColumnFilters(t, filters)).map(t => ({
          'Invoice Charge Date': t.created_on ? t.created_on.slice(0, 10) : '',
          'TPA': t.tpa || '',
          'Reporting Period': t.reporting_period || '',
          'Amount': t.amount || '',
          'Credit Amount': t.credit_amount != null ? parseFloat(t.credit_amount).toFixed(2) : '',
          'Credit Description': t.credit_description || '',
          'Credit Date': t.credit_date || '',
          'Reason for Adjustment': t.reason_for_adjustment || '',
          'Currency Type': t.currency_type || '',
          'Source': t.credit_amount != null ? 'Manual' : (t.source || ''),
          'Charge Type': t.charge_type || '',
          'Billing Type': t.billing_type || '',
          'Invoice Date': t.invoice_date ? t.invoice_date.slice(0, 10) : '',
          'Billing Transaction Date': t.billing_transaction_date || '',
          'LimeIQ Key': t.bdx_key || '',
        }))
        const XLSX = await import('xlsx')
        const ws = XLSX.utils.json_to_sheet(rows)
        const wb = XLSX.utils.book_new()
        XLSX.utils.book_append_sheet(wb, ws, 'Invoice Charges')
        XLSX.writeFile(wb, `invoice-charges-${bdxKey}.xlsx`)
      } catch (err) {
        fetchError.value = (err && err.message) || 'Failed to download invoice charges.'
      } finally {
        downloading.value = false
      }
    }

    return {
      showModal,
      isSuperuser,
      loading,
      fetchError,
      transactions,
      sortState,
      onSortChanged,
      columnFilters,
      colFilterInput,
      activeColumnFilters,
      applyColFilter,
      clearColFilter,
      filteredTransactions,
      availableChargePeriods,
      formatReportingPeriod,
      totalCount,
      currentPage,
      pageSize,
      deletingId,
      downloading,
      showForm,
      showDeleteConfirm,
      editingTransaction,
      creditingTransaction,
      deletingItem,
      deleteReason,
      deleteError,
      deletePreview,
      deletePreviewLoading,
      deleteScope,
      allSplitsDeletable,
      canDelete,
      submitting,
      formError,
      form,
      tableFields,
      onModalShow,
      onModalHide,
      close,
      handlePageChange,
      availablePeriods,
      loadingPeriods,
      openAddForm,
      openEditForm,
      openCreditForm,
      viewingTransaction,
      openViewCredit,
      closeViewCredit,
      cancelForm,
      handleFormSubmit,
      openDeleteConfirm,
      cancelDelete,
      handleDelete,
      handleDownload,
      onDragStart,
      BDX_CURRENCY_TYPES
    }
  }
})
</script>

<style scoped>
.delete-splits-table {
  font-size: 0.85rem;
}

.delete-splits-current {
  font-weight: 600;
}

>>> .modal-body {
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  user-select: text !important;
  padding-top: 8px;
}
>>> .modal-header .drag-handle {
  cursor: grab;
  color: #aaa;
  font-size: 1.1rem;
  flex-shrink: 0;
  padding: 0 4px;
  line-height: 1;
  -webkit-user-select: none;
  user-select: none;
}
>>> .modal-header .drag-handle:active {
  cursor: grabbing;
}
>>> .modal-header .drag-title {
  -webkit-user-select: text !important;
  -moz-user-select: text !important;
  user-select: text !important;
  cursor: text !important;
}

>>> .charge-sort-icons {
  display: inline-flex;
  flex-direction: column;
  line-height: 1;
  vertical-align: middle;
  gap: 0;
}
>>> .charge-sort-icons .b-icon {
  font-size: 0.45rem;
  display: block;
}
>>> .charge-sort-icons .sort-active {
  color: #343a40;
}
>>> .charge-sort-icons .sort-inactive {
  color: #ced4da;
}
>>> .charge-filter-btn {
  color: #6c757d;
  line-height: 1;
}
>>> .charge-filter-btn:hover {
  color: #343a40;
}

>>> .charge-table {
  border: none !important;
  border-collapse: collapse !important;
}
>>> .charge-table thead th {
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: 2px solid #dee2e6 !important;
  padding: 8px 14px !important;
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #6c757d;
  font-weight: 700;
  background: transparent !important;
  white-space: nowrap;
}
>>> .charge-table tbody td {
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: 1px solid #f0f0f0 !important;
  padding: 10px 14px !important;
  vertical-align: middle !important;
  font-size: 0.85rem;
  color: #343a40;
  background: transparent !important;
}
>>> .charge-table tbody tr:last-child td {
  border-bottom: none !important;
}
>>> .charge-table tbody tr:hover td {
  background-color: #f8f9fa !important;
}

.limeiq-primary-btn {
  background-color: #1c4a50 !important;
  border-color: #1c4a50 !important;
  color: #fff !important;
}
.limeiq-primary-btn:hover:not(:disabled) {
  background-color: #163c41 !important;
  border-color: #163c41 !important;
}

.charge-action-icon {
  color: #1c4a50;
}
.charge-action-icon:hover {
  color: #163c41;
}

.charge-section-title {
  color: #1c4a50;
  font-size: 1.2rem;
  font-weight: 600;
}
</style>
